
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SunStatus } from '@/types/terrace';
import { getStockholmHour } from '@/lib/utils/timezoneUtils';

interface SunForecastCardProps {
  sunStatus: SunStatus;
  lastUpdated?: string;
  isUpdating?: boolean;
  onManualUpdate?: () => void;
  canAutoUpdate?: boolean;
  updateConditions?: string;
}

const SunForecastCard = ({ 
  sunStatus, 
  lastUpdated,
  isUpdating = false,
  onManualUpdate,
  canAutoUpdate = false,
  updateConditions = ''
}: SunForecastCardProps) => {
  const formatLastUpdated = (timestamp: string | undefined) => {
    if (!timestamp) return 'Never updated';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  const getSunStatusText = (status: SunStatus) => {
    switch (status) {
      case 'sunny':
        return 'This terrace will likely have sun for the next 60 minutes based on current conditions.';
      case 'soon':
        return 'This terrace will likely have sun within the next 30 minutes based on current conditions.';
      case 'not-sunny':
        return 'This terrace is not expected to have sun in the next hour based on current conditions.';
      default:
        return '';
    }
  };

  const handleManualUpdate = () => {
    if (onManualUpdate && !isUpdating) {
      // Prevent event bubbling that might cause page refresh
      onManualUpdate();
    }
  };

  const stockholmHour = getStockholmHour();
  const isInUpdateWindow = stockholmHour >= 6 && stockholmHour < 22;
  const lastUpdateAge = lastUpdated ? Math.floor((Date.now() - new Date(lastUpdated).getTime()) / (1000 * 60)) : null;
  const isDataOld = lastUpdateAge && lastUpdateAge > 30;

  return (
    <Card className="mb-6 bg-accent/20">
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-medium">AI Sun Forecast</h3>
          {onManualUpdate && isInUpdateWindow && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualUpdate}
              disabled={isUpdating}
              className="text-xs"
              type="button"
            >
              {isUpdating ? 'Updating...' : 'Refresh'}
            </Button>
          )}
        </div>
        
        <p className="text-muted-foreground mb-3">
          {getSunStatusText(sunStatus)}
        </p>
        
        <div className="text-xs text-sunset-medium space-y-1">
          <div>
            Last checked: {formatLastUpdated(lastUpdated)}
            {isDataOld && (
              <span className="text-orange-500 ml-1">
                (outdated)
              </span>
            )}
          </div>
          
          {/* Debug information for development */}
          {import.meta.env.DEV && (
            <div className="mt-2 p-2 bg-gray-100 rounded text-xs space-y-1">
              <div>Stockholm time: {stockholmHour}:00</div>
              <div>Update window: {isInUpdateWindow ? '✅ Open (6-22)' : '❌ Closed (6-22)'}</div>
              <div>Auto-update: {canAutoUpdate ? '✅ Ready' : '❌ ' + updateConditions}</div>
              {lastUpdateAge && <div>Data age: {lastUpdateAge} minutes</div>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SunForecastCard;
